import React from 'react';
import { SunriseIcon, BookOpenIcon, MapIcon, FrownIcon } from 'lucide-react';
interface LearnScreenProps {
  openChapter: (id: string) => void;
}
export function LearnScreen({
  openChapter
}: LearnScreenProps) {
  const chapters = [{
    id: 'welcome',
    num: <SunriseIcon className="w-6 h-6" data-id="element-170" />,
    tag: 'Welcome',
    title: 'The Day the Mirror Lied No More',
    sub: "Why your body isn't broken — it's just stagnant.",
    time: '3 min read',
    progress: 100,
    bg: 'from-[#E8F4EA] to-[#C8E6CC]',
    color: 'text-[#2C6A2C]'
  }, {
    id: 'ch1',
    num: '1',
    tag: 'Chapter 1',
    title: 'The Myth of the "Magic Juice"',
    sub: 'Why your previous detoxes failed — and made you feel worse.',
    time: '5 min read',
    progress: 80,
    bg: 'from-[#FFF0E8] to-[#FFD5B8]',
    color: 'text-[#8A4020]'
  }, {
    id: 'ch2',
    num: '2',
    tag: 'Chapter 2',
    title: 'The Emunctories',
    sub: "Your body's 5 waste management exits — and how to open them.",
    time: '6 min read',
    progress: 60,
    bg: 'from-[#E8F0FF] to-[#C8D8FF]',
    color: 'text-[#2040A0]'
  }, {
    id: 'ch3',
    num: '3',
    tag: 'Chapter 3',
    title: 'The 7-Day Reset',
    sub: 'Step-by-step breakdown of every phase and the science behind it.',
    time: '8 min read',
    progress: 30,
    bg: 'from-[#FFF4D0] to-[#FFE098]',
    color: 'text-[#8A6000]'
  }, {
    id: 'conclusion',
    num: <SunriseIcon className="w-6 h-6" data-id="element-171" />,
    tag: 'Conclusion',
    title: 'You Are Now in Flow',
    sub: "You've stopped fighting your body. Now you work with it.",
    time: '3 min read',
    progress: 0,
    bg: 'from-[#E8FFFA] to-[#B8EEDD]',
    color: 'text-[#006040]'
  }];
  return <div className="flex flex-col min-h-screen pb-24 animate-fade-in" data-id="element-172">
      <div className="bg-gradient-to-br from-[#3A2040] via-[#5A3A6A] to-[#8A6A9A] px-5 pt-6 pb-8" data-id="element-173">
        <div className="text-white/70 text-[13px] mb-1" data-id="element-174">The Full Book</div>
        <div className="text-white text-2xl font-extrabold mb-1.5" data-id="element-175">
          The Knowledge Base
        </div>
        <div className="text-white/70 text-sm" data-id="element-176">
          Everything you need to understand why this works
        </div>
      </div>

      <div className="px-5 py-4 flex flex-col gap-3.5" data-id="element-177">
        {chapters.map(ch => <div key={ch.id} onClick={() => openChapter(ch.id)} className="bg-white rounded-[20px] overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.06)] cursor-pointer transition-transform active:scale-[0.99]" data-id="element-178">
            <div className="p-[18px] px-5 flex gap-3.5 items-start" data-id="element-179">
              <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${ch.bg} ${ch.color} flex items-center justify-center text-lg font-extrabold shrink-0`} data-id="element-180">
                {ch.num}
              </div>
              <div className="flex-1" data-id="element-181">
                <div className="text-[10px] text-text-light uppercase tracking-widest font-semibold mb-1" data-id="element-182">
                  {ch.tag}
                </div>
                <div className="text-base font-bold text-text-dark mb-1" data-id="element-183">
                  {ch.title}
                </div>
                <div className="text-[13px] text-text-mid leading-relaxed" data-id="element-184">
                  {ch.sub}
                </div>
                <div className="flex items-center gap-2.5 mt-2" data-id="element-185">
                  <div className="text-[11px] text-text-light flex items-center gap-1" data-id="element-186">
                    <BookOpenIcon className="w-3 h-3" data-id="element-187" /> {ch.time}
                  </div>
                  <div className="flex-1 h-1 bg-cream rounded-full overflow-hidden" data-id="element-188">
                    <div className="h-full bg-sage-dark rounded-full" style={{
                  width: `${ch.progress}%`
                }} data-id="element-189"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>)}

        {/* Pain-to-Pleasure Map */}
        <div className="bg-white rounded-[20px] p-5 mt-1 shadow-[0_2px_12px_rgba(0,0,0,0.06)]" data-id="element-190">
          <div className="text-base font-extrabold text-text-dark mb-1 flex items-center gap-2" data-id="element-191">
            <MapIcon className="w-5 h-5 text-sage-dark" data-id="element-192" /> Pain → Pleasure Map
          </div>
          <div className="text-[13px] text-text-mid mb-4" data-id="element-193">
            How each symptom is solved in this program
          </div>

          <div className="flex flex-col gap-3" data-id="element-194">
            {[{
            icon: FrownIcon,
            pain: 'Puffy face on waking',
            sol: '2-min Facial Lymphatic Drainage Protocol',
            gain: 'Sculpted face + rested eyes'
          }, {
            icon: FrownIcon,
            pain: 'Bloating after every meal',
            sol: 'Strategic Micro-Drainage supplementation',
            gain: 'Flat belly within 4 hours of eating'
          }, {
            icon: FrownIcon,
            pain: 'Detox headaches & breakouts',
            sol: 'Phase 1: Opening Emunctories First',
            gain: 'Deep detox with ZERO discomfort'
          }, {
            icon: FrownIcon,
            pain: 'Brain fog & mid-day crashes',
            sol: 'Hepatic filter cleaning protocol',
            gain: 'Mental clarity from 9 AM to 7 PM'
          }, {
            icon: FrownIcon,
            pain: 'Cellulite & fluid retention',
            sol: 'Pro Dry Brushing technique',
            gain: 'Firmer skin + active lymphatic system'
          }].map((item, i) => <div key={i} className="bg-cream rounded-xl p-3.5" data-id="element-195">
                <div className="text-xs text-sage-dark font-bold mb-1 flex items-center gap-1.5" data-id="element-196">
                  <item.icon className="w-4 h-4" data-id="element-197" /> {item.pain}
                </div>
                <div className="text-[13px] text-text-mid" data-id="element-198">→ {item.sol}</div>
                <div className="text-xs text-sage-dark font-semibold mt-1 flex items-center gap-1.5" data-id="element-199">
                  <SunriseIcon className="w-3.5 h-3.5" data-id="element-200" /> {item.gain}
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}